import { createContext, useContext, useEffect, useState, useCallback, useMemo } from "react";
import { i18nApi, I18N_NAMESPACES, LANG_STORAGE_KEY, closestEnabled } from "@/lib/i18n";

const I18nContext = createContext(null);

const RTL = new Set(["ar", "he", "fa", "ur"]);

// Locale tags for Intl formatting (sensible regional defaults for Indian languages).
const LOCALE_TAG = {
  en: "en-US", hi: "hi-IN", bn: "bn-IN", or: "or-IN", mr: "mr-IN", gu: "gu-IN",
  pa: "pa-IN", ta: "ta-IN", te: "te-IN", kn: "kn-IN", ml: "ml-IN", as: "as-IN",
  ar: "ar", fr: "fr-FR", de: "de-DE", es: "es-ES", zh: "zh-CN",
};

export function I18nProvider({ children }) {
  const [languages, setLanguages] = useState([]);
  const [lang, setLangState] = useState(localStorage.getItem(LANG_STORAGE_KEY) || "en");
  const [packs, setPacks] = useState({});      // { "namespace.key": value }
  const [ready, setReady] = useState(false);

  // 1) Load enabled languages once; pick initial language (stored → browser → default).
  useEffect(() => {
    let active = true;
    i18nApi.languages()
      .then((d) => {
        if (!active) return;
        setLanguages(d.items || []);
        const codes = (d.items || []).map((l) => l.code);
        const stored = localStorage.getItem(LANG_STORAGE_KEY);
        if (stored && codes.includes(stored)) {
          setLangState(stored);
        } else {
          const detected = closestEnabled(navigator.language, codes);
          setLangState(detected || d.default || "en");
        }
      })
      .catch(() => setLanguages([{ code: "en", name: "English", nativeName: "English", direction: "ltr" }]));
    return () => { active = false; };
  }, []);

  // 2) Load packs whenever the language changes; apply <html lang/dir>.
  useEffect(() => {
    let active = true;
    Promise.all(I18N_NAMESPACES.map((ns) => i18nApi.pack(lang, ns).catch(() => ({ entries: {} }))))
      .then((results) => {
        if (!active) return;
        const merged = {};
        // Pack entry keys are already fully-qualified (e.g. "auth.welcome", "nav.dashboard").
        results.forEach((res) => {
          Object.entries(res.entries || {}).forEach(([k, v]) => { merged[k] = v; });
        });
        setPacks(merged);
        setReady(true);
      });
    const dir = RTL.has(lang) ? "rtl" : "ltr";
    document.documentElement.lang = lang;
    document.documentElement.dir = dir;
    localStorage.setItem(LANG_STORAGE_KEY, lang);
    return () => { active = false; };
  }, [lang]);

  // Translate: key is "namespace.key" (e.g. "auth.signIn"). Falls back to provided default.
  const t = useCallback(
    (key, fallback) => packs[key] ?? fallback ?? key,
    [packs]
  );

  const setLang = useCallback((code) => {
    setLangState(code);
    i18nApi.setPreference(code).catch(() => { /* anonymous/login screen — local only */ });
  }, []);

  const tag = LOCALE_TAG[lang] || "en-US";
  const dir = RTL.has(lang) ? "rtl" : "ltr";

  const fmt = useMemo(() => ({
    number: (n, opts) => new Intl.NumberFormat(tag, opts).format(n ?? 0),
    currency: (n, currency = "INR") =>
      new Intl.NumberFormat(tag, { style: "currency", currency, maximumFractionDigits: 0 }).format(n ?? 0),
    date: (d, opts = { dateStyle: "medium" }) => {
      try { return new Intl.DateTimeFormat(tag, opts).format(new Date(d)); }
      catch { return String(d ?? ""); }
    },
  }), [tag]);

  const value = useMemo(
    () => ({ t, lang, dir, tag, languages, setLang, ready, fmt }),
    [t, lang, dir, tag, languages, setLang, ready, fmt]
  );

  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export const useI18n = () => {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used within I18nProvider");
  return ctx;
};
